import type { Dispatch, SetStateAction } from 'react'
import SelectCustom, { type SelectOption } from '../../../components/SelectCustom'
import type { AddressEditorState } from './addressBookHelpers'
import type { AddressLabels } from './addressBookConfig'

type AddressEditorFormFieldsProps = {
  editorState: AddressEditorState
  setEditorState: Dispatch<SetStateAction<AddressEditorState>>
  labels: AddressLabels
  countryOptions: SelectOption[]
  isItalian: boolean
}

export const AddressEditorFormFields = ({
  editorState,
  setEditorState,
  labels,
  countryOptions,
  isItalian,
}: AddressEditorFormFieldsProps) => (
  <div className="grid gap-3">
    <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
      {labels.label}
      <input
        value={editorState.label}
        onChange={(event) =>
          setEditorState((current) => ({
            ...current,
            label: event.target.value,
          }))
        }
        className="ca-input h-9 px-3 text-sm"
      />
    </label>

    <div className="grid gap-3 sm:grid-cols-2">
      <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
        {labels.firstName}
        <input
          value={editorState.firstName}
          onChange={(event) =>
            setEditorState((current) => ({
              ...current,
              firstName: event.target.value,
            }))
          }
          className="ca-input h-9 px-3 text-sm"
        />
      </label>
      <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
        {labels.lastName}
        <input
          value={editorState.lastName}
          onChange={(event) =>
            setEditorState((current) => ({
              ...current,
              lastName: event.target.value,
            }))
          }
          className="ca-input h-9 px-3 text-sm"
        />
      </label>
    </div>

    <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
      {labels.addressLine}
      <input
        value={editorState.addressLine}
        onChange={(event) =>
          setEditorState((current) => ({
            ...current,
            addressLine: event.target.value,
          }))
        }
        className="ca-input h-9 px-3 text-sm"
      />
    </label>

    <div className="grid gap-3 sm:grid-cols-3">
      <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
        {labels.city}
        <input
          value={editorState.city}
          onChange={(event) =>
            setEditorState((current) => ({
              ...current,
              city: event.target.value,
            }))
          }
          className="ca-input h-9 px-3 text-sm"
        />
      </label>
      <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
        {labels.postalCode}
        <input
          value={editorState.postalCode}
          onChange={(event) =>
            setEditorState((current) => ({
              ...current,
              postalCode: event.target.value,
            }))
          }
          className="ca-input h-9 px-3 text-sm"
        />
      </label>
      <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
        {labels.province}
        <input
          value={editorState.province}
          onChange={(event) =>
            setEditorState((current) => ({
              ...current,
              province: event.target.value,
            }))
          }
          className="ca-input h-9 px-3 text-sm"
        />
      </label>
    </div>

    <div className="grid gap-3 sm:grid-cols-2">
      <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
        {labels.country}
        <SelectCustom
          value={editorState.country}
          onChange={(value) =>
            setEditorState((current) => ({
              ...current,
              country: value,
            }))
          }
          ariaLabel={labels.country}
          options={countryOptions}
          searchable
          searchPlaceholder={isItalian ? 'Cerca paese' : 'Search country'}
          emptyLabel={isItalian ? 'Nessun paese trovato' : 'No country found'}
          size="sm"
          buttonClassName="h-9 rounded-[10px] border-(--color-border-strong) text-(--color-text-primary) text-sm"
        />
      </label>
      <label className="grid gap-1 text-sm font-medium text-(--color-text-secondary)">
        {labels.phone}
        <input
          value={editorState.phone}
          onChange={(event) =>
            setEditorState((current) => ({
              ...current,
              phone: event.target.value,
            }))
          }
          className="ca-input h-9 px-3 text-sm"
        />
      </label>
    </div>

    <fieldset className="rounded-xl border border-(--color-border-default) bg-(--color-bg-surface-muted) px-3 py-3">
      <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-(--color-text-muted)">{labels.roleHint}</legend>
      <div className="mt-1 grid gap-2">
        <label className="inline-flex items-center gap-2 text-sm text-(--color-text-secondary)">
          <input
            type="checkbox"
            checked={editorState.useShipping}
            onChange={(event) =>
              setEditorState((current) => ({
                ...current,
                useShipping: event.target.checked,
              }))
            }
            className="h-4 w-4 rounded border-(--color-border-strong) text-(--color-action-primary) focus:ring-(--color-focus-ring)"
          />
          <span>{labels.useShipping}</span>
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-(--color-text-secondary)">
          <input
            type="checkbox"
            checked={editorState.useBilling}
            onChange={(event) =>
              setEditorState((current) => ({
                ...current,
                useBilling: event.target.checked,
              }))
            }
            className="h-4 w-4 rounded border-(--color-border-strong) text-(--color-action-primary) focus:ring-(--color-focus-ring)"
          />
          <span>{labels.useBilling}</span>
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-(--color-text-secondary)">
          <input
            type="checkbox"
            checked={editorState.setDefault}
            onChange={(event) =>
              setEditorState((current) => ({
                ...current,
                setDefault: event.target.checked,
              }))
            }
            className="h-4 w-4 rounded border-(--color-border-strong) text-(--color-action-primary) focus:ring-(--color-focus-ring)"
          />
          <span>{labels.setDefault}</span>
        </label>
      </div>
    </fieldset>
  </div>
)
